import { useTranslation } from 'react-i18next'
import Skeleton from '@/shared/components/Skeleton/Skeleton'
import { AreaChart } from '@/features/dashboard/Charts/AreaChart/AreaChart'
import { LoyaltyStats as LoyaltyStatsModel } from '../../types/loyalty.model'
import styles from './LoyaltyStats.module.scss'

interface LoyaltyStatsProps {
  stats: LoyaltyStatsModel
}

/**
 * The four counters, built from the same condensed AreaChart the menu tab uses
 * for its own headline numbers, so the two pages read as one product.
 *
 * Every figure is a server-side aggregate and there is no loyalty endpoint yet,
 * so the value renders as an em dash and the plot is empty. A zero here would be
 * a claim about the venue rather than about our data, and an invented series
 * would be a lie with a shape.
 */
/*
  Shaped like what the tile will hold — a figure and a plot — rather than the word
  "loading", and sized for the condensed card, which is 160px tall. AreaChart
  draws it over the card and keeps the title, so the tile stays readable.
*/
const statPlaceholder = (
  <div className="flex absolute flex-col h-full w-full p-3 gap-2">
    <div className="w-full pt-[1.75rem]">
      <Skeleton variant="rounded" width="45%" height="1.5rem" />
    </div>
    <div className="flex w-full flex-1 pb-1">
      <Skeleton variant="rounded" width="100%" height="100%" />
    </div>
  </div>
)

function LoyaltyStats({ stats }: LoyaltyStatsProps) {
  const { t } = useTranslation()

  const describe = (value?: number, suffix = '') => (
    <div className={styles.statValue}>
      {value === undefined ? (
        <span className={`title-bold-large ${styles.statEmpty}`}>—</span>
      ) : (
        <span className="title-bold-large">
          {value.toLocaleString()}
          {suffix}
        </span>
      )}
    </div>
  )

  /*
    Four counters, one of which is the reason an owner opens this page at all:
    how much of the venue's revenue came through the QR code. It is marked with
    the app's accent rather than moved or resized — the row stays a row.
  */
  const tiles = [
    {
      key: 'collecting',
      title: t('LOYALTY.STATS.COLLECTING'),
      value: stats.collectingGuests,
      description: describe(stats.collectingGuests)
    },
    {
      key: 'returning',
      title: t('LOYALTY.STATS.RETURNING'),
      value: stats.returningSharePercent,
      description: describe(stats.returningSharePercent, '%')
    },
    {
      key: 'qrRevenue',
      title: t('LOYALTY.STATS.QR_REVENUE'),
      value: stats.qrRevenueSharePercent,
      description: describe(stats.qrRevenueSharePercent, '%'),
      highlighted: true
    },
    {
      key: 'redeemed',
      title: t('LOYALTY.STATS.REDEEMED'),
      value: stats.rewardsRedeemed,
      description: describe(stats.rewardsRedeemed)
    }
  ]

  return (
    <div className={styles.statsRow}>
      {tiles.map(tile => (
        <AreaChart
          key={tile.key}
          title={tile.title}
          description={tile.description}
          chartsData={[]}
          condensed
          // Not "loading" in the usual sense: there is no loyalty endpoint to
          // load from. Same rendering either way — the placeholder stands in
          // until a figure exists, and the tile fills itself once one does.
          isLoading={tile.value === undefined}
          loadingPlaceholder={statPlaceholder}
          cardClassName={tile.highlighted ? styles.statHighlighted : undefined}
        />
      ))}
    </div>
  )
}

export default LoyaltyStats
